import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useSharedSelector } from "../../../../store/SharedProvider";
import { selectRoomMissions } from "../../../../store/room/room-selectors";
import { getColorFromString } from "../../../../utils/mission-color";

const ScheduleEntryMission: React.FC<{
  missionId: string;
  onMissionIdChange: (newId: string) => void;
  CustomFixedButton?: React.ReactElement;
  helpText?: string;
  roomId: string
}> = ({ missionId, onMissionIdChange, CustomFixedButton, helpText, roomId }) => {
  const { t } = useTranslation();

  const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const roomMissions = useSharedSelector((state) => selectRoomMissions(state, roomId));

  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(missionId);
  const [page, setPage] = useState(0);

  const itemsPerPage = 8;
  const otherPageMissions = roomMissions.slice(itemsPerPage);
  const pageCount = 1 + Math.ceil(otherPageMissions.length / itemsPerPage);
  const paginatedMissions = roomMissions.sort((a, b) => { return a.name > b.name ? 1 : -1; }).slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage);

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 0));
  const handleNext = () => setPage((prev) => Math.min(prev + 1, pageCount - 1));

  const missionChangeHandler = (newId: string | null) => {
    if (newId == null) return;
    setSelectedMissionId(newId);
    onMissionIdChange(newId);
  };

  return (
    <React.Fragment>
      <ToggleButtonGroup
        value={selectedMissionId}
        exclusive
        onChange={(event, newValue) => missionChangeHandler(newValue)}
        size="small"
        sx={(theme) => ({
          pt: 2,
          width: "100%",
          display: "grid",
          gridTemplateColumns: matchesSmUp ? "repeat(3, 1fr)" : "1fr",
          minHeight: theme.spacing(5),
        })}
      >
        {CustomFixedButton != null ? (
          CustomFixedButton
        ) : (
          <ToggleButton
            value="new"
            sx={(theme) => ({
              fontSize: matchesSmUp ? "0.87rem" : "small",
              fontWeight: "bold",
              color: selectedMissionId === "new" ? `${theme.palette.primary.main}CC !important` : "GrayText",
            })}
          >
            <AddIcon fontSize="small" sx={{ mr: 1 }} />{matchesSmUp ? t("button-create-mission") : t("button-new")}
          </ToggleButton>
        )}
        {paginatedMissions.map((mission) => (
          <ToggleButton
            key={mission.id}
            value={mission.id}
            sx={(theme) => ({
              borderLeft: `${theme.spacing(1)} ${getColorFromString(mission.color, "0.3")} solid !important`,
              color: selectedMissionId === mission.id ? `${theme.palette.primary.main}CC !important` : "GrayText",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              padding: 1,
              fontWeight: "bold",
              fontSize: "0.87rem",
              textTransform: "none",
              textAlign: "left",
            })}
          >
            {mission.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Grid container>
        <Grid item xs={12} md={6} display="flex" alignItems="center">
          {(missionId === "" || helpText != null) && (
            <FormHelperText error={helpText == null}>{helpText ?? t("select-mission-to-continue")}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={6} display="flex" justifyContent="end" alignItems="center">
          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={1} gap={2}>
              <IconButton onClick={handlePrev} disabled={page === 0}>
                <KeyboardArrowLeftRoundedIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2">
                {page + 1} / {pageCount}
              </Typography>
              <IconButton onClick={handleNext} disabled={page === pageCount - 1}>
                <KeyboardArrowRightRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default ScheduleEntryMission;
